// KEEP THIS FILE IN SYNC WITH fruitful-all/apps/backend/src/modules/user/nameUtils.ts

// Characters that don't decompose to base + combining mark under NFD; map to ASCII equivalent
const ADDITIONAL_ACCENT_MAP: Record<string, string> = {
    '\u00D8': 'O', // Ø
    '\u00F8': 'o', // ø
    '\u00C5': 'A', // Å
    '\u00E5': 'a', // å
    '\u00DE': 'Th', // Þ
    '\u00FE': 'th', // þ
};

// Allowed for validation: Unicode letters (including accents), spaces, hyphens, single quote variants.
const ALLOWED_NAME_PATTERN = /^[\p{L}\s\-'\u2018\u2019\u201A\u201B]*$/u;

/**
 * Throws if the name contains unresolvable errors: any character other than
 * alphabetical (including accents), spaces, hyphens, or single quote variants.
 * If isLegal is true, also throws when the trimmed name length is < 2.
 */
export const validateName = (
    name: string | null | undefined,
    isLegal: boolean,
): void => {
    if (name == null || typeof name !== 'string' || name.trim() === '') {
        throw new Error('Name is required');
    }
    if (!ALLOWED_NAME_PATTERN.test(name)) {
        throw new Error('Name contains invalid characters');
    }
    const trimmed = name.trim();
    if (isLegal && trimmed.length < 2) {
        throw new Error('Legal name must be at least 2 characters');
    }
};

/**
 * Validates the name, then standardizes whitespace. If isLegal is true,
 * also flattens accented characters and normalizes single quote variants to ASCII.
 */
export const sanitizeName = (
    name: string | null | undefined,
    isLegal: boolean,
): string => {
    validateName(name, isLegal);
    if (name == null || typeof name !== 'string') {
        throw new Error('Name is required');
    }
    const s = name.trim().split(/\s+/).join(' ');
    if (!isLegal) {
        return s;
    }
    // Flatten accented characters (explicit map first, then NFD + strip diacritics)
    let result = s;
    for (const [char, replacement] of Object.entries(ADDITIONAL_ACCENT_MAP)) {
        result = result.split(char).join(replacement);
    }
    result = result.normalize('NFD').replace(/\p{Diacritic}/gu, '');
    // Exotic single quotes → ASCII apostrophe
    result = result.replace(/[\u2018\u2019\u201A\u201B]/g, "'");
    return result;
};
